import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export type CustomerState = "ACTIVE" | "INACTIVE" | "BLOCKED" | string;
export type CustomerStatusAction = "ACTIVATE" | "INACTIVE" | "BLOCKED";

export interface Customer {
  id: string | number;
  companyName?: string | null;
  name?: string;
  surname?: string | null;
  type?: string;
  clientType?: {
    id?: string | number;
    name?: string;
  } | null;
  customerManager?: string;
  clientManager?: {
    id?: string | number;
    name?: string;
  } | null;
  managerName?: string;
  email?: string;
  mail?: string;
  devices?: number;
  deviceCount?: number;
  devicesCount?: number;
  sensors?: number;
  sensorCount?: number;
  fuelSensorsCount?: number;
  state?: CustomerState;
  status?:
    | CustomerState
    | {
        id?: string | number;
        name?: string;
      }
    | null;
  [key: string]: unknown;
}

export interface CustomerListParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sort?: "ASC" | "DESC";
  search?: string;
  status?: string;
  distributorId?: string | number;
  type?: string;
  [key: string]: unknown;
}

export interface CustomerListResponse {
  customers?: Customer[];
  clients?: Customer[];
  items?: Customer[];
  data?: Customer[];
  totalItems?: number;
  total?: number;
  page?: number;
  size?: number;
}

export interface CustomerStatsResponse {
  total?: number;
  all?: number;
  allCustomers?: number;
  totalCustomers?: number;
  active?: number;
  activeCustomers?: number;
  inactive?: number;
  inactiveCustomers?: number;
  blocked?: number;
  blockedCustomers?: number;
  ACTIVE?: number;
  INACTIVE?: number;
  BLOCKED?: number;
  [key: string]: unknown;
}

export type CustomerDashboardResponse =
  | CustomerStatsResponse
  | CustomerStatsResponse[];

export interface CustomerDetailResponse {
  client?: Customer;
  devices?: unknown[];
  units?: unknown[];
  vehicles?: unknown[];
  [key: string]: unknown;
}

const getNumberValue = (value: unknown, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const getOptionalNumberValue = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0
    ? numberValue
    : undefined;
};

const getStringValue = (value: unknown, fallback = "") =>
  value === undefined || value === null ? fallback : String(value);

const getNestedId = (record: Customer, key: string, fallbackKey: string) => {
  const nestedValue = record[key];

  if (nestedValue && typeof nestedValue === "object" && "id" in nestedValue) {
    return getNumberValue((nestedValue as { id?: unknown }).id);
  }

  return getNumberValue(record[fallbackKey]);
};

export const buildCustomerStatusPayload = (
  record: Customer,
  active: boolean,
) => {
  const distributorId = getOptionalNumberValue(record.distributorId);

  return {
    client: {
      id: getNumberValue(record.id),
      companyName: getStringValue(record.companyName),
      name: getStringValue(record.name),
      surname: getStringValue(record.surname),
      email: getStringValue(record.email ?? record.mail),
      personalCode: getStringValue(record.personalCode),
      ...(distributorId ? { distributorId } : {}),
      clientTypeId: getNestedId(record, "clientType", "clientTypeId"),
      clientPresetId: getNestedId(record, "clientPreset", "clientPresetId"),
      statusId: getNestedId(record, "status", "statusId"),
      clientManagerId: getNestedId(record, "clientManager", "clientManagerId"),
      comment: getStringValue(record.comment),
      sourceId: getNestedId(record, "source", "sourceId"),
      sourceComments: getStringValue(record.sourceComments),
      competitorsId: getNestedId(record, "competitor", "competitorsId"),
      legalName: getStringValue(record.legalName),
      businessTypeIds: Array.isArray(record.businessTypeIds)
        ? record.businessTypeIds.map((id) => getNumberValue(id))
        : [],
      regNo: getStringValue(record.regNo),
      address: getStringValue(record.address),
      billAddress: getStringValue(record.billAddress),
      handlingInPlace: getStringValue(record.handlingInPlace),
      vat: getStringValue(record.vat),
      bank: getStringValue(record.bank),
      accountNo: getStringValue(record.accountNo),
      swift: getStringValue(record.swift),
      active,
      createdBy: getStringValue(record.createdBy),
      updatedBy: getStringValue(record.updatedBy),
      blocked: !active,
      blockedMessage: getStringValue(record.blockedMessage),
      ourBankAccount: getStringValue(record.ourBankAccount),
      accountNumber: getStringValue(record.accountNumber),
      languageId: getNestedId(record, "language", "languageId"),
    },
    invoiceSetting: {
      id: getNumberValue(record.invoiceSettingId),
      userVat: Boolean(record.userVat),
      clientId: getNumberValue(record.id),
      countryId: getNestedId(record, "country", "countryId"),
      paymentTypeId: getNestedId(record, "paymentType", "paymentTypeId"),
      paymentPeriodId: getNestedId(record, "paymentPeriod", "paymentPeriodId"),
      paySubscriptionId: getNestedId(
        record,
        "paySubscription",
        "paySubscriptionId",
      ),
      pprId: getNestedId(record, "ppr", "pprId"),
      active: true,
      createDate: getStringValue(record.invoiceCreateDate),
      createdBy: getStringValue(record.createdBy),
      updateDate: getStringValue(record.invoiceUpdateDate),
      updatedBy: getStringValue(record.updatedBy),
    },
    userInterfaceDetails: {
      id: getNumberValue(record.userInterfaceDetailsId),
      currencyId: getNestedId(record, "currency", "currencyId"),
      distanceId: getNestedId(record, "distance", "distanceId"),
      volumeId: getNestedId(record, "volume", "volumeId"),
      clientId: getNumberValue(record.id),
      temperatureId: getNestedId(record, "temperature", "temperatureId"),
      weightId: getNestedId(record, "weight", "weightId"),
      fuelConsumptionId: getNestedId(
        record,
        "fuelConsumption",
        "fuelConsumptionId",
      ),
      active: true,
      createdBy: getStringValue(record.createdBy),
      updatedBy: getStringValue(record.updatedBy),
    },
    productPricing: Array.isArray(record.productPricing)
      ? record.productPricing
      : [],
    contact: {
      id: getNumberValue(record.contactId),
      clientId: getNumberValue(record.id),
      name: getStringValue(record.contactName ?? record.name),
      surname: getStringValue(record.contactSurname ?? record.surname),
      email: getStringValue(record.contactEmail ?? record.email ?? record.mail),
      phoneNumber: getStringValue(record.phoneNumber),
      positionId: getNestedId(record, "position", "positionId"),
      positionName: getStringValue(record.positionName),
      active: true,
      createDate: getStringValue(record.contactCreateDate),
      createdBy: getStringValue(record.createdBy),
      updateDate: getStringValue(record.contactUpdateDate),
      updatedBy: getStringValue(record.updatedBy),
    },
  };
};

export const getCustomersList = (
  params: CustomerListParams = {},
  language?: string,
) => {
  return ApiService.get<{ response: CustomerListResponse }>(
    ServicesUrlEndpoints.CUSTOMER.LISTING,
    {
      params,
      ...getLanguageHeaders(language),
    },
  );
};

export const getCustomersStats = (
  params: Record<string, unknown> = {},
  language?: string,
) => {
  return ApiService.get<{ response: CustomerDashboardResponse }>(
    ServicesUrlEndpoints.CUSTOMER.DASHBOARD,
    {
      params,
      ...getLanguageHeaders(language),
    },
  );
};

export const getCustomerById = (id: string | number, language?: string) => {
  return ApiService.get<{ response: CustomerDetailResponse }>(
    ServicesUrlEndpoints.CUSTOMER.GET.replace("{clientId}", String(id)),
    {
      ...getLanguageHeaders(language),
    },
  );
};

export const createCustomer = (
  payload: Record<string, unknown>,
  language?: string,
) => {
  return ApiService.post(ServicesUrlEndpoints.CUSTOMER.ADD, payload, {
    ...getLanguageHeaders(language),
  });
};

export const updateCustomer = (
  id: string | number,
  payload: Record<string, unknown>,
  language?: string,
) => {
  return ApiService.patch(
    ServicesUrlEndpoints.CUSTOMER.UPDATE.replace("{clientId}", String(id)),
    payload,
    {
      ...getLanguageHeaders(language),
    },
  );
};

export const patchCustomerStatus = (
  id: string | number,
  status: CustomerStatusAction,
  language?: string,
) => {
  return ApiService.patch(
    ServicesUrlEndpoints.CUSTOMER.STATUS.replace(
      "{clientId}",
      String(id),
    ),
    undefined,
    {
      params: { status },
      ...getLanguageHeaders(language),
    },
  );
};

export const deleteCustomer = (id: string | number, language?: string) => {
  return ApiService.delete(
    ServicesUrlEndpoints.CUSTOMER.DELETE.replace("{id}", String(id)),
    {
      ...getLanguageHeaders(language),
    },
  );
};

export const bulkDeleteCustomers = (
  ids: Array<string | number>,
  language?: string,
) => {
  return Promise.all(ids.map((id) => deleteCustomer(id, language)));
};
